/**
 * Cliente HTTP único de toda a camada services/. Todos os *.service.ts chamam
 * o default export com { method, url, data, useToken, hasFile }:
 *   • useToken — injeta Authorization: Bearer <JWT do localStorage>;
 *   • hasFile  — muda o Content-Type para multipart/form-data (uploads).
 *
 * O interceptor de resposta normaliza QUALQUER erro para o shape AppError
 * ({ success, type: 'message'|'validation', message, statusCode }) — os
 * componentes nunca veem AxiosError cru. JWT inválido/expirado limpa o
 * localStorage e redireciona para /entrar?expirada=1 (exceto se já estiver
 * na tela de login, evitando loop). statusCode permite distinguir casos como
 * o 402 de upgrade bloqueado na página /conta.
 */
import axios, { AxiosError, AxiosResponse, Method } from 'axios';

import authToken from './auth_token.service';

import { AppError } from 'entities/Errors';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const axiosError: AxiosError<AppError> = error;
      if (!axiosError.response) {
        return Promise.reject({
          type: 'message',
          success: false,
          message:
            'Ocorreu uma falha na conexão com o servidor! Tente novamente mais tarde.',
        } as AppError);
      }
      if (['message', 'validation'].includes(axiosError.response.data.type)) {
        if (
          axiosError.response.data.type === 'message' &&
          ['Invalid JWT Token.', 'JWT Token is missing.'].includes(
            axiosError.response.data.message
          )
        ) {
          // Evita loop de redirect quando já está na tela de login e sinaliza
          // a expiração para a página exibir o aviso ao usuário
          if (!window.location.pathname.startsWith('/entrar')) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/entrar?expirada=1';
          }
        }
        return Promise.reject({
          ...axiosError.response.data,
          statusCode: axiosError.response.status,
        });
      }
    }
    // Fallback: só se chega aqui em casos raros — erro não-Axios (bug no próprio
    // cliente) ou resposta com formato inesperado (type ≠ message/validation).
    // Mensagem PRÓPRIA e distinta do erro 500 do backend, para os dois nunca se
    // confundirem num print/relato; e loga o erro cru no console para inspeção no
    // DevTools (antes era silenciosamente engolido).
    console.error('[api] Erro não tratado pelo interceptor:', error);
    const status = axios.isAxiosError(error)
      ? error.response?.status
      : undefined;
    const err = {
      success: false,
      type: 'message',
      message: status
        ? `Resposta inesperada do servidor (HTTP ${status}). Tente novamente; se o problema persistir, contate o suporte.`
        : 'Erro inesperado no aplicativo. Recarregue a página; se o problema persistir, contate o suporte.',
    } as AppError;
    return Promise.reject(err);
  }
);

export type ApiResponse<T> = Promise<AxiosResponse<T>>;

interface ApiProps {
  method: Method;
  url: string;
  data?: object;
  useToken?: boolean;
  hasFile?: boolean;
  params?: object;
}

export default ({ method, url, data, params, useToken, hasFile }: ApiProps) =>
  api({
    method,
    ...(params && { params }),
    url,
    ...(data && { data }),
    headers: {
      'Referrer-Policy': 'strict-origin-when-cross-origin',
      ...(useToken && { Authorization: `Bearer ${authToken()}` }),
      ...(hasFile && { 'Content-Type': 'multipart/form-data' }),
    },
  });
